<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { api, ApiError } from '@/api/client'
import UiModal from '@/components/ui/UiModal.vue'
import { expandIcons } from '@/content/expandIcons'
import { iconNames } from '@/content/contentIcons'
import type { AdminContentByName } from '@/api/types'

// In-place editor for one content region, opened from a <ContentText> ✎. Loads the block's RAW content
// by name (or seeds from the region's coded default when no block exists yet), lets an admin edit the
// raw HTML with a live preview + an "Insert icon" helper, and upserts it. On save the parent refreshes
// the region from the public (sanitized) endpoint, so what renders is exactly what anon users see.
const props = defineProps<{ name: string; defaultHtml?: string }>()
const emit = defineEmits<{ saved: []; cancel: [] }>()

const open = ref(true)
const content = ref('')
const loading = ref(true)
const saving = ref(false)
const error = ref<string | null>(null)
const insertPick = ref('')
const taRef = ref<HTMLTextAreaElement | null>(null)

const icons = iconNames()
const previewHtml = computed(() => expandIcons(content.value))

onMounted(async () => {
  try {
    const dto = await api.get<AdminContentByName>(
      `/api/v1/admin/content/by-name/${encodeURIComponent(props.name)}`,
    )
    content.value = dto.exists && dto.content != null ? dto.content : (props.defaultHtml ?? '')
  } catch {
    content.value = props.defaultHtml ?? ''
  } finally {
    loading.value = false
  }
})

function insertIcon() {
  const pick = insertPick.value
  insertPick.value = ''
  if (!pick) return
  const token = `[[icon:${pick}]]`
  const ta = taRef.value
  if (ta) {
    const start = ta.selectionStart ?? content.value.length
    const end = ta.selectionEnd ?? content.value.length
    content.value = content.value.slice(0, start) + token + content.value.slice(end)
  } else {
    content.value += token
  }
}

async function save() {
  saving.value = true
  error.value = null
  try {
    const r = await api.put<{ ok: boolean; errors: string[] }>(
      `/api/v1/admin/content/by-name/${encodeURIComponent(props.name)}`,
      { content: content.value },
    )
    if (r.ok) {
      open.value = false
      emit('saved')
    } else {
      error.value = r.errors?.join(' ') || 'Save failed.'
    }
  } catch (e) {
    error.value = e instanceof ApiError ? e.message : 'Save failed.'
  } finally {
    saving.value = false
  }
}

function cancel() {
  open.value = false
  emit('cancel')
}
</script>

<template>
  <UiModal :model-value="open" :title="`Edit content: ${name}`" max-width="780px" @update:model-value="cancel">
    <p v-if="error" class="form-error">{{ error }}</p>
    <p v-if="loading" class="u-muted">Loading…</p>
    <template v-else>
      <div class="cie-toolbar">
        <label class="field-label" style="margin: 0">Content (raw HTML)</label>
        <span class="cie-spacer"></span>
        <select class="select cie-iconsel" v-model="insertPick" @change="insertIcon">
          <option value="">Insert icon…</option>
          <option v-for="n in icons" :key="n" :value="n">{{ n }}</option>
        </select>
      </div>
      <textarea ref="taRef" v-model="content" class="textarea cie-ta" rows="12" spellcheck="false"></textarea>
      <p class="cie-hint">
        Insert a glyph with <code>[[icon:name]]</code>. Content is sanitized on display (scripts and
        disallowed tags are stripped). Inline <code>style</code> is removed on save — use approved
        classes from the
        <a href="/admin/style-guide" target="_blank" rel="noopener" class="text-accent">Style Guide</a>.
      </p>
      <div class="cie-preview-wrap">
        <div class="cie-preview-label">Preview</div>
        <div class="cie-preview" v-html="previewHtml"></div>
      </div>
    </template>
    <template #footer>
      <div class="cie-actions">
        <button class="btn" :disabled="saving || loading" @click="save">{{ saving ? 'Saving…' : 'Save' }}</button>
        <button class="btn-text" :disabled="saving" @click="cancel">Cancel</button>
      </div>
    </template>
  </UiModal>
</template>

<style scoped>
.cie-toolbar { display: flex; align-items: center; gap: 10px; margin-bottom: 6px; }
.cie-spacer { flex: 1; }
.cie-iconsel { width: auto; min-width: 150px; font-size: 0.85em; padding: 4px 8px; }
.cie-ta { width: 100%; font-family: ui-monospace, Menlo, Consolas, monospace; font-size: 13px; line-height: 1.5; }
.cie-hint { color: var(--slate, #5b7185); font-size: 12px; margin: 6px 0 14px; }
.cie-hint code { background: #e7eef5; padding: 1px 5px; }
.cie-preview-wrap { border: 1px solid var(--rule, #c9d8e6); overflow: hidden; }
.cie-preview-label {
  font-size: 10.5px; text-transform: uppercase; letter-spacing: 0.16em; font-weight: 700;
  color: var(--slate-2, #7089a0); background: #eef3f8; padding: 6px 12px;
  border-bottom: 1px solid var(--rule, #c9d8e6);
}
.cie-preview { padding: 16px; max-height: 320px; overflow-y: auto; background: #fff; }
.cie-actions { display: flex; gap: 10px; }
</style>
